import { filter, forEach, get, has, indexOf, isEmpty, map, mapKeys, omit, reduce } from 'lodash';
import {
  ADD_DOMAIN_CUSTODIAN_SUCCESS,
  ADD_DOMAIN_SUCCESS,
  APPROVE_MARKETPLACE_REQUEST_SUCCESS,
  DELETE_DOMAIN_CUSTODIAN_SUCCESS,
  DELETE_DOMAIN_ITEM_SUCCESS,
  DELETE_DOMAIN_SUCCESS,
  GET_ALL_DOMAINS_SUCCESS,
  GET_DOMAIN_CUSTODIANS_SUCCESS,
  GET_DOMAIN_ITEMS_SUCCESS,
  GET_INCOMING_ACCESS_REQUESTS_SUCCESS,
  GET_INCOMING_LISTING_REQUESTS_SUCCESS,
  GET_OUTGOING_ACCESS_REQUESTS_SUCCESS,
  GET_OUTGOING_LISTING_REQUESTS_SUCCESS,
  REJECT_MARKETPLACE_REQUEST_SUCCESS,
  UPDATE_DOMAIN_ITEM_ACCESS_SUCCESS,
} from '../../constants/actionTypes';

const initialState = {
  lastUpdated: null,
  byId: [],
  itemsByDomain: {},
};

export const domainsReducer = (state = initialState, action) => {
  switch (action.type) {
    case GET_ALL_DOMAINS_SUCCESS:
      return saveDomains(state, action);

    case DELETE_DOMAIN_SUCCESS:
      return removeDomain(state, action);

    case GET_DOMAIN_ITEMS_SUCCESS:
      return saveDomainItems(state, action);

    case GET_OUTGOING_ACCESS_REQUESTS_SUCCESS:
      return saveOutgoingAccessRequests(state, action);

    case GET_INCOMING_ACCESS_REQUESTS_SUCCESS:
      return saveIncomingAccessRequests(state, action);

    case GET_OUTGOING_LISTING_REQUESTS_SUCCESS:
      return saveOutgoingListingRequests(state, action);

    case GET_INCOMING_LISTING_REQUESTS_SUCCESS:
      return saveIncomingListingRequests(state, action);

    case APPROVE_MARKETPLACE_REQUEST_SUCCESS:
    case REJECT_MARKETPLACE_REQUEST_SUCCESS:
      return removeMarketplaceRequest(state, action);

    case UPDATE_DOMAIN_ITEM_ACCESS_SUCCESS:
      return updateOutgoingAccessRequests(state, action);

    case DELETE_DOMAIN_ITEM_SUCCESS:
      return deleteDomainItem(state, action);

    case GET_DOMAIN_CUSTODIANS_SUCCESS:
    case ADD_DOMAIN_CUSTODIAN_SUCCESS:
      return saveDomainCustodians(state, action);

    case DELETE_DOMAIN_CUSTODIAN_SUCCESS:
      return removeDomainCustodian(state, action);

    default:
      return state;
  }
};

export default domainsReducer;

function saveDomains(state, action) {
  const { result } = action;

  return {
    ...state,
    lastUpdated: action.receivedAt,
    byId: result,
    error: null,
  };
}

function removeDomain(state, action) {
  const {
    requestInfo: { domainId },
  } = action;
  const { byId } = state;

  let newById = filter(byId, (id) => id !== domainId);

  return {
    ...state,
    lastUpdated: action.receivedAt,
    byId: newById,
    error: null,
  };
}

function saveDomainItems(state, action) {
  const {
    result,
    requestInfo: { id: domainId },
  } = action;

  return {
    ...state,
    itemsByDomain: {
      ...state.itemsByDomain,
      [domainId]: result,
    },
  };
}

function deleteDomainItem(state, action) {
  const {
    requestInfo: { domainId, itemId },
  } = action;
  const { itemsByDomain } = state;
  const newItemsList = filter(itemsByDomain[domainId] || [], (id) => id !== itemId);

  return {
    ...state,
    itemsByDomain: {
      ...itemsByDomain,
      [domainId]: newItemsList,
    },
  };
}

function saveOutgoingAccessRequests(state, action) {
  const { result } = action;

  return {
    ...state,
    outgoingAccessRequests: result,
  };
}

function saveIncomingAccessRequests(state, action) {
  const { result } = action;

  return {
    ...state,
    incomingAccessRequests: result,
  };
}

function saveOutgoingListingRequests(state, action) {
  const { result } = action;

  return {
    ...state,
    outgoingListingRequests: result,
  };
}

function saveIncomingListingRequests(state, action) {
  const { result } = action;

  return {
    ...state,
    incomingListingRequests: result,
  };
}

function removeMarketplaceRequest(state, action) {
  const {
    requestInfo: { id: requestId, requestType },
  } = action;

  if (requestType === 'marketplace_item_access') {
    const { incomingAccessRequests } = state;
    const newAccessApprovals = filter(incomingAccessRequests, (item) => item.id !== requestId);

    return {
      ...state,
      incomingAccessRequests: newAccessApprovals,
    };
  } else if (requestType === 'marketplace_item') {
    const { incomingListingRequests } = state;
    const newListingApprovals = filter(incomingListingRequests, (item) => item.id !== requestId);

    return {
      ...state,
      incomingListingRequests: newListingApprovals,
    };
  } else {
    return state;
  }
}

function updateOutgoingAccessRequests(state, action) {
  const { result } = action;
  const { outgoingAccessRequests } = state;

  if (!outgoingAccessRequests.some((request) => request.id === result.id)) {
    return {
      ...state,
      outgoingAccessRequests: [...outgoingAccessRequests, result],
    };
  }

  return {
    ...state,
  };
}

function saveDomainCustodians(state, action) {
  const {
    result,
    receivedAt,
    requestInfo: { domainId },
  } = action;

  let { custodiansByDomain } = state || {};
  const custodians = map(result, (r) => {
    return { ...r, custodian_type: 'domain' };
  });
  const custodiansById = mapKeys(custodians, 'id');
  const newDomainCustodians = {
    ...custodiansByDomain,
    [domainId]: {
      lastFetched: receivedAt,
      custodiansById,
    },
  };

  return {
    ...state,
    custodiansByDomain: newDomainCustodians,
  };
}

function removeDomainCustodian(state, action) {
  const {
    receivedAt,
    requestInfo: { domainId, userId },
  } = action;
  const { custodiansByDomain } = state;
  const domainCustodians = custodiansByDomain[domainId] || {
    custodiansById: {},
  };
  const { custodiansById } = domainCustodians;

  const newCustodiansById = omit(custodiansById, userId);

  const newState = {
    ...state,
    custodiansByDomain: {
      ...custodiansByDomain,
      [domainId]: {
        ...domainCustodians,
        lastFetched: receivedAt,
        custodiansById: newCustodiansById,
      },
    },
  };

  return newState;
}
